import type { ReactNode } from "react";
import { Code, FileText, Settings2 } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { StepSqlBlock } from "../StepSqlBlock";
import { getString, getNumber, getBool } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getExtractSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const type = getString(config.type) || "table";
  const sql = getString(config.sql);
  const columns = Array.isArray(config.columns) ? (config.columns as string[]) : [];
  const batchSize = getNumber(config.batchSize);
  const where = getString(config.where);
  const incremental = config.incremental as Record<string, unknown> | undefined;

  if (type === "sql" && sql) {
    items.push(
      <StepSqlBlock
        key="sql"
        icon={<Code className="size-3.5" />}
        label={t("summaries.extract.label_sql")}
        sql={sql}
      />
    );
  } else if (type === "mql") {
    const collection = getString(config.collection);
    const mql = getString(config.mql);
    if (collection) {
      items.push(
        <StepInfoRow key="collection" icon={<FileText className="size-3.5" />} label={t("summaries.extract.label_collection")} value={collection} mono />
      );
    }
    if (mql) {
      items.push(
        <StepSqlBlock key="mql" icon={<Code className="size-3.5" />} label={t("summaries.extract.label_mql")} sql={mql} />
      );
    }
  } else {
    if (columns.length > 0) {
      items.push(
        <StepInfoRow key="columns" icon={<FileText className="size-3.5" />} label={t("summaries.extract.label_columns")} value={t("summaries.extract.columns_count", { count: columns.length })} />
      );
    }
    if (where) {
      items.push(
        <StepSqlBlock key="where" icon={<Code className="size-3.5" />} label={t("summaries.extract.label_where")} sql={where} />
      );
    }
  }

  if (batchSize) {
    items.push(
      <StepInfoRow key="batch" icon={<Settings2 className="size-3.5" />} label={t("summaries.extract.label_batch")} value={t("summaries.extract.batch_rows", { count: batchSize.toLocaleString() })} />
    );
  }

  if (incremental && getBool(incremental.enabled)) {
    items.push(
      <StepInfoRow key="incr" icon={<Settings2 className="size-3.5" />} label={t("summaries.extract.label_incremental")} value={`${incremental.field} (${incremental.type})`} mono />
    );
  }

  return items;
}
